import React, {useEffect, useState} from 'react';
import {useGroupContext} from "../../../contexts/group_context";
import {Group, GroupCreate, GroupType,} from '../../../schemas';
import {FiBriefcase, FiEdit2, FiTrash2, FiCalendar, FiCheck, FiX, FiLock} from 'react-icons/fi';
import {IoIosAddCircleOutline} from "react-icons/io";
import Popup from "../../../components/SharedComponents/Popup";
import {Border, Brand, Ink, Motion, Radii, Shadows, Surface, ThemeColors} from "../../../colors";
import Button from "../../../components/SharedComponents/Button/Button";
import {createGroup, updateGroup, deleteGroup} from "../../../api";
import Swal from "sweetalert2";
import {AppView} from "../../../App";
import {useNavigationContext} from "../../../contexts/NavContext/nav-context";
import {withApiErrorHandlerThrow} from "../../../error_handling";
import moment from "moment";

const CreateWorkspacePopup: React.FC<{ close: () => void }> = ({close}) => {
    const {
        refreshUserGroups,
    } = useGroupContext()!;
    const {navigateTo} = useNavigationContext()!;

    const [formGroupName, setFormGroupName] = useState<string>('');
    const [loading, setLoading] = useState(false);

    const handleDataSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoading(true); // Start loading

        const createGroupData: GroupCreate = {
            name: formGroupName,
            type: GroupType.CASE,
        };
        const response: Group = await withApiErrorHandlerThrow(
            () => createGroup(createGroupData),
            {titleQualifier: "Error Creating Group", navigateTo: navigateTo}
        );
        if (response) {
            // Refresh groups from server to get the newly created group
            await refreshUserGroups();
            setLoading(false);
            Swal.fire({
                title: "Success",
                text: "Workspace created successfully",
                icon: "success",

            }).then(() => {
                setFormGroupName('');
                close();
            });
        } else {
            console.error("Failed to create group");
        }
    };

    return (
        <div
            className={`w-full ${Radii.xl} p-6 ${Surface.base} ${Border.DEFAULT} ${Shadows.soft}`}
            style={{maxHeight: 'calc(100vh - 2rem)'}}
        >
            <form onSubmit={handleDataSubmit}>
                <label className={`block text-xs font-medium uppercase tracking-wide ${Ink.muted} mb-1.5`}>
                    Workspace name
                </label>
                <input
                    type="text"
                    className={`w-full px-3 py-2.5 ${Border.DEFAULT} ${Radii.md} ${Ink.primary} placeholder:text-zinc-400 ${Brand.ring} ${Motion.fast}`}
                    value={formGroupName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormGroupName(e.target.value)}
                    placeholder="Enter workspace name"
                />

                <div className={`mt-4 mb-5 flex items-center gap-2 ${Radii.md} ${Surface.sunken} ${Border.soft} px-3 py-2.5`}>
                    <FiLock className="text-[#4b8f5a] flex-shrink-0"/>
                    <span className={`text-xs ${Ink.secondary}`}>
                        Files are encrypted at rest with AES-256
                    </span>
                </div>

                <Button title="Create Workspace" loading={loading}/>
            </form>
        </div>
    );
};

const CreateWorkspace: React.FC = () => {
    const [formPopup, setFormPopup] = React.useState(false);

    return (
        <>
            {(formPopup &&
                <div className={" "}>
                    <Popup isOpen={formPopup} title={"Create Workspace Upload"}
                           close={() => setFormPopup(false)}>
                        <CreateWorkspacePopup
                            close={() => setFormPopup(false)}/>
                    </Popup>
                </div>
            )}

            <div
                onClick={() => setFormPopup(true)}
                className={`group flex flex-col items-center justify-center w-full min-h-[180px] cursor-pointer
                    ${Radii.lg} border-2 border-dashed border-[#d1d1d8] ${Surface.base} p-8 ${Shadows.sm}
                    hover:border-[#9c83cf] hover:bg-[#f7f5fc] hover:shadow-[0_8px_24px_-14px_rgba(106,78,170,0.25)]
                    ${Motion.base}`}
            >
                <IoIosAddCircleOutline className="text-[#7e63bf] group-hover:text-[#564191] mb-3 w-[3em] h-[3em] transition-colors"/>
                <span className={`font-semibold ${Ink.secondary} group-hover:text-[#42316f] transition-colors`}>Create new workspace</span>
            </div>
        </>
    );
};

// Component to display an individual group/workspace
const WorkspacePreview: React.FC<{ group: Group; onRefresh: () => void }> = ({group, onRefresh}) => {
    const {navigateTo} = useNavigationContext()!;
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(group.name);
    const [loading, setLoading] = useState(false);

    const reviewRound = group.review_round;

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return 'Unknown';
        return moment(dateStr).format('MMM D, YYYY');
    };

    const handleEdit = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isEditing) {
            // Save the edit
            if (editName.trim() && editName !== group.name) {
                setLoading(true);
                try {
                    await withApiErrorHandlerThrow(
                        () => updateGroup(group.id, editName.trim()),
                        {titleQualifier: "Error Updating Workspace", navigateTo}
                    );
                    await onRefresh();
                    await Swal.fire({
                        title: "Success",
                        text: "Workspace name updated",
                        icon: "success",
                        timer: 1500,
                        showConfirmButton: false,
                    });
                } finally {
                    setLoading(false);
                }
            }
            setIsEditing(false);
        } else {
            setEditName(group.name);
            setIsEditing(true);
        }
    };

    const handleDelete = async (e: React.MouseEvent) => {
        e.stopPropagation();

        // First warning
        const firstConfirm = await Swal.fire({
            title: "Delete Workspace?",
            html: `<p>Are you sure you want to delete <strong>${group.name}</strong>?</p>
                   <p class="text-red-600 mt-2">This will permanently delete all documents, tags, and data in this workspace.</p>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: ThemeColors.neutralDarkHex,
            confirmButtonText: "Yes, delete it",
            cancelButtonText: "Cancel",
        });

        if (!firstConfirm.isConfirmed) return;

        // Second warning - type workspace name
        const secondConfirm = await Swal.fire({
            title: "Final Confirmation",
            html: `<p>This action <strong>cannot be undone</strong>.</p>
                   <p class="mt-2">Type <strong>${group.name}</strong> to confirm deletion:</p>`,
            icon: "error",
            input: "text",
            inputPlaceholder: "Type workspace name here",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: ThemeColors.neutralDarkHex,
            confirmButtonText: "Delete Forever",
            cancelButtonText: "Cancel",
            inputValidator: (value) => {
                if (value !== group.name) {
                    return "Workspace name doesn't match. Please type it exactly.";
                }
                return null;
            },
        });

        if (!secondConfirm.isConfirmed) return;

        // Third and final warning
        const finalConfirm = await Swal.fire({
            title: "LAST CHANCE",
            html: `<p class="text-xl text-red-600 font-bold">This is your final warning!</p>
                   <p class="mt-2">You are about to permanently delete:</p>
                   <ul class="text-left mt-2 ml-4">
                     <li>• ${group.document_count} document(s)</li>
                     <li>• ${group.tags?.length || 0} tag(s)/folder(s)</li>
                   </ul>
                   <p class="mt-4 font-bold">Are you absolutely sure?</p>`,
            icon: "error",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: ThemeColors.neutralDarkHex,
            confirmButtonText: "DELETE PERMANENTLY",
            cancelButtonText: "Cancel",
        });

        if (!finalConfirm.isConfirmed) return;

        // Actually delete
        setLoading(true);
        void Swal.fire({
            title: "Deleting workspace...",
            text: "Removing all documents, tags, and data. This may take a moment.",
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => Swal.showLoading(),
        });
        try {
            await withApiErrorHandlerThrow(
                () => deleteGroup(group.id),
                {titleQualifier: "Error Deleting Workspace", navigateTo}
            );
            await onRefresh();
            Swal.close();
            await Swal.fire({
                title: "Deleted",
                text: "Workspace has been permanently deleted.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });
        } catch {
            Swal.close();
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleEdit(e as unknown as React.MouseEvent);
        } else if (e.key === 'Escape') {
            setIsEditing(false);
            setEditName(group.name);
        }
    };

    return (
        <div
            onClick={() => {
                if (!isEditing) {
                    navigateTo({
                        view: AppView.FolderView,
                        group: group,
                    });
                }
            }}
            className={`group/card relative flex flex-col justify-between w-full cursor-pointer
                ${Radii.lg} ${Surface.base} ${Border.DEFAULT} ${Shadows.sm} p-5
                hover:border-[#bcabe0] hover:shadow-[0_8px_24px_-14px_rgba(106,78,170,0.25)]
                ${Motion.base}`}
        >
            {/* Action buttons */}
            <div className="absolute top-2.5 right-2.5 flex gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity">
                <button
                    onClick={handleEdit}
                    disabled={loading}
                    className={`p-1.5 ${Radii.md} ${Brand.soft} ${Brand.softHover} ${Motion.fast}`}
                    title={isEditing ? "Save" : "Edit name"}
                >
                    <FiEdit2 className="text-sm"/>
                </button>
                <button
                    onClick={handleDelete}
                    disabled={loading}
                    className={`p-1.5 ${Radii.md} bg-red-50 text-red-600 hover:bg-red-100 ${Motion.fast}`}
                    title="Delete workspace"
                >
                    <FiTrash2 className="text-sm"/>
                </button>
            </div>

            {/* Top: Workspace info */}
            <div className="flex items-start gap-3">
                <div className={`p-2.5 ${Radii.md} ${Brand.soft} flex-shrink-0`}>
                    <FiBriefcase className="text-xl"/>
                </div>
                <div className="flex flex-col min-w-0 flex-1 gap-1">
                    {isEditing ? (
                        <>
                            <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onKeyDown={handleKeyDown}
                                onClick={(e) => e.stopPropagation()}
                                className={`font-medium ${Ink.primary} ${Border.DEFAULT} ${Radii.md} px-2 py-1.5 text-sm w-full ${Brand.ring} ${Motion.fast}`}
                                placeholder="Workspace name"
                                autoFocus
                            />
                            <div className="flex gap-1.5 mt-1">
                                <button
                                    onClick={handleEdit}
                                    disabled={loading}
                                    className={`flex items-center gap-1 px-2.5 py-1 text-xs font-semibold ${Brand.solid} ${Brand.solidHover} ${Radii.md} ${Motion.fast} disabled:opacity-50`}
                                >
                                    <FiCheck className="text-xs"/>
                                    Save
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsEditing(false);
                                        setEditName(group.name);
                                    }}
                                    className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium ${Ink.secondary} ${Surface.sunken} hover:bg-[#e8e8ec] ${Radii.md} ${Motion.fast}`}
                                >
                                    <FiX className="text-xs"/>
                                    Cancel
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <span className={`font-semibold ${Ink.primary} break-words whitespace-normal`}>
                                {group.name}
                            </span>
                            <span className={`text-xs ${Ink.muted} break-words whitespace-normal`}>
                                Review round {reviewRound}
                            </span>
                        </>
                    )}
                </div>
            </div>

            {/* Middle: Stats */}
            <div className="mt-4 flex flex-col gap-1">
                <span className={`text-sm ${Ink.secondary}`}>
                    {group.users_in_group.length} user{group.users_in_group.length !== 1 ? "s" : ""}
                </span>
                <span className={`text-sm ${Ink.secondary}`}>
                    {group.document_count} document{group.document_count !== 1 ? "s" : ""}
                </span>
            </div>

            {/* Created at and encryption */}
            <div className={`mt-3 flex items-center justify-between text-xs ${Ink.subtle}`}>
                <div className="flex items-center gap-1">
                    <FiCalendar className="text-xs"/>
                    <span>Created {formatDate(group.created_at)}</span>
                </div>
                <div className="flex items-center gap-1 text-[#4b8f5a]" title="Encrypted at rest (AES-256)">
                    <FiLock className="text-xs"/>
                    <span>Encrypted</span>
                </div>
            </div>

            {/* Document count */}
            <div className="mt-3 flex items-center justify-end">
                <span className={`text-sm ${Ink.secondary}`}>
                    {group.document_count} document{group.document_count !== 1 ? "s" : ""} total
                </span>
            </div>
        </div>
    );
};

// Component to display all user's groups/workspaces
const WorkspacePreviews: React.FC = () => {
    const {userGroups, refreshUserGroups} = useGroupContext()!;

    // Refresh groups from server when component mounts to ensure fresh data
    useEffect(() => {
        void refreshUserGroups();
    }, []);

    return (
        <div className="p-6 overflow-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {userGroups?.filter((group) => group.type == GroupType.CASE).map(group => (
                    <WorkspacePreview key={group.id} group={group} onRefresh={refreshUserGroups}/>
                ))}
                <CreateWorkspace/>
            </div>
        </div>
    );
};

export default WorkspacePreviews;